import { useState, useEffect } from "react";
import { api } from "../lib/config";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, CreditCard, Wallet, Banknote, Smartphone, CheckCircle2, History, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// iter216 (2026-03-01) — Cash / Cash on Arrival removed as a selectable
// option per owner directive. Stripe Checkout is the default. Historical
// bookings with `cash_on_arrival` still display correctly via the label
// map elsewhere but the option is not offered here.
const METHODS = [
  { key: "card_on_file",    label: "Card on File",                 Icon: ShieldCheck, tone: "text-emerald-700",
    hint: "Auto-charged AFTER each completed service" },
  { key: "stripe",          label: "Stripe Checkout",              Icon: CreditCard, tone: "text-violet-700" },
  { key: "zelle",           label: "Zelle",                        Icon: Wallet,     tone: "text-indigo-700" },
  { key: "cashapp",         label: "Cash App",                     Icon: Wallet,     tone: "text-emerald-700" },
  { key: "venmo",           label: "Venmo",                        Icon: Wallet,     tone: "text-sky-700" },
  { key: "applepay",        label: "Apple Pay",                    Icon: Smartphone, tone: "text-gray-800" },
  { key: "googlepay",       label: "Google Pay",                   Icon: Smartphone, tone: "text-blue-700" },
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function ChangePaymentMethodDialog({ open, onOpenChange, customer, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [note, setNote] = useState("");

  const current = customer?.preferred_payment_method || customer?.recurring_schedule?.payment_method || "cash_on_arrival";

  useEffect(() => {
    if (!open || !customer?.user_id) return;
    setNote("");
    (async () => {
      try {
        const { data } = await api.get(`/admin/customers/${customer.user_id}/payment-method-history`);
        setHistory(data.history || []);
      } catch (_e) { setHistory([]); }
    })();
  }, [open, customer?.user_id]);

  const submit = async (method) => {
    // Treat `cash` and `cash_on_arrival` as the same method to avoid a
    // no-op error when older records use `cash`.
    const alias = { cash: "cash_on_arrival" };
    if ((alias[method] || method) === (alias[current] || current)) {
      toast.info("Already set to this method"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/customers/${customer.user_id}/payment-method`, {
        payment_method: method, note: note || undefined,
      });
      toast.success("Payment method updated successfully.");
      onChanged?.(data);
      // Refresh history
      const { data: h } = await api.get(`/admin/customers/${customer.user_id}/payment-method-history`);
      setHistory(h.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Change failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="change-payment-method-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] tracking-tight">Change Payment Method</DialogTitle>
          <DialogDescription>
            Updates {customer?.name || "the customer"}&apos;s default method + all future unpaid visits.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional) — e.g. customer requested via phone"
            className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm"
            data-testid="pm-change-note"
          />

          <div className="grid grid-cols-2 gap-2">
            {METHODS.map((m) => {
              const isActive = m.key === current || (m.aliases || []).includes(current);
              return (
                <button
                  key={m.key}
                  onClick={() => submit(m.key)}
                  disabled={busy}
                  data-testid={`pm-option-${m.key}`}
                  className={`text-left p-3 rounded-xl border-2 transition-all disabled:opacity-50 ${
                    isActive
                      ? "border-[#047857] bg-emerald-50 ring-2 ring-emerald-100"
                      : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <m.Icon className={`w-4 h-4 ${m.tone}`} />
                      <span className="text-[12px] font-extrabold text-gray-900">{m.label}</span>
                    </div>
                    {isActive && <CheckCircle2 className="w-4 h-4 text-[#047857]" />}
                  </div>
                  {m.hint && (
                    <div className="mt-1 text-[10px] leading-tight text-emerald-800/80">
                      {m.hint}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {busy && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Updating…
            </div>
          )}

          {/* Audit history */}
          <div className="mt-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-1.5">
              <History className="w-3 h-3" /> Change History
            </div>
            {history.length === 0 ? (
              <div className="text-[12px] text-gray-400 italic">No changes yet.</div>
            ) : (
              <div className="max-h-40 overflow-auto space-y-1 pr-1" data-testid="pm-history-list">
                {history.map((h, i) => (
                  <div key={i} className="text-[11px] text-gray-700 bg-gray-50 rounded-lg px-2 py-1.5" data-testid={`pm-history-row-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-extrabold text-gray-900">{h.old_label || "—"} → {h.new_label}</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{h.actor}</span>
                    </div>
                    <div className="text-[10px] text-gray-500">{fmtDate(h.at)} · by {h.changed_by}{h.note ? ` — ${h.note}` : ""}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pt-2 flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pm-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
